import React, { createContext, useContext, useEffect, useState } from "react";
import { ContextValueType, PropsType } from "../types/index";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { API } from "../constant/api";
import useAuthAxios from "../utils/baseAxios";
import { errorMessage } from "../utils/errorMessage";
import { useAuth } from "./auth-context";
import { useLocation } from "react-router-dom";
export const UserContext = createContext<ContextValueType>({});
export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<PropsType> = ({ children }) => {
  const location = useLocation();
  const [isNotified, setIsNotified] = useState<boolean>(false);
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState<string>("/dashboard");
  const [resetEmail, setResetEmail] = useState<string>("");
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const [bnNumber, setBnNumber] = useState("");
  const [identityUrl, setIdentityUrl] = useState("");
  const [addressUrl, setAddressUrl] = useState("");
  const [cacUrl, setCacUrl] = useState("");
  const [cacfile, setCacFile] = useState<File | null>(null);
  const [addyfile, setAddyFile] = useState<File | null>(null);
  const [idfile, setIdFile] = useState<File | null>(null);

  const [businessVerified, setBusinessVerified] = useState(false);
  const [documentNotloaded, setDocumentNotloaded] = useState(false);
  const [activeStatus, setActiveStatus] = useState<number>(1);
  const [paymentRefId, setPaymentRefId] = useState<string>("");
  const [paymentName, setPaymentName] = useState<string>("");

  const axiosInstance = useAuthAxios();

  /**
   * One request for the signed-in user. This used to be two identical queries
   * under separate keys ("merchantDetails" and "merchant-details"), each
   * disabled and kicked off by its own effect — so every load fetched
   * api/users/me twice. `userDetails`/`refetch` and `merchantDetails`/
   * `refetch2` are kept as aliases because both names are read across the app.
   */
  const {
    data: userDetails,
    error: userError,
    refetch,
  } = useQuery({
    queryKey: ["merchantDetails"],
    queryFn: async () => {
      const response = await axiosInstance.get(API.getUser);
      return response.data.data;
    },
    // Replaces the disabled-query + refetch-in-an-effect pattern; React Query
    // starts the request itself once auth-context has confirmed a session. That
    // bootstrap already seeds this key, so this usually resolves from cache.
    enabled: isAuthenticated,
    retry: 1,
  });

  useEffect(() => {
    if (userError) {
      const newError = userError as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [userError]);

  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setActiveStatus(1);
    } else if (location.pathname === "/payment") {
      setActiveStatus(3);
    } else if (location.pathname === "/sales") {
      setActiveStatus(5);
    } else if (location.pathname === "/activity") {
      setActiveStatus(4);
    }
  }, [location.pathname]);

  return (
    <UserContext.Provider
      value={{
        cacfile,
        setCacFile,
        addyfile,
        setAddyFile,
        idfile,
        setIdFile,

        setIsOpen,
        isOpen,
        isNotified,
        setIsNotified,
        userDetails,
        refetch,
        page,
        setPage,
        resetEmail,
        setResetEmail,
        uploadModal,
        setUploadModal,
        bnNumber,
        setBnNumber,
        identityUrl,
        setIdentityUrl,
        addressUrl,
        setAddressUrl,
        cacUrl,
        setCacUrl,
        setDocumentNotloaded,
        documentNotloaded,
        businessVerified,
        setBusinessVerified,
        successModal,
        setSuccessModal,
        paymentRefId,
        setPaymentRefId,
        activeStatus,
        setActiveStatus,
        paymentName,
        setPaymentName,
        // Aliases of the same query — see the comment on the useQuery above.
        merchantDetails: userDetails,
        refetch2: refetch,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
